/**
 * The remembered target language for "Translate result" in the brainstorm
 * panel — the same shape as the model picker's per-function memory
 * (features/ai-models/pick.ts), for the same reason: a pick made once should
 * hold for the next answer and the next session, and a browser that refuses
 * storage still gets it for this panel's lifetime.
 *
 * The first-ever default comes from the BROWSER's language, matched against
 * the translate dialog's curated list: the exact tag first, then the primary
 * subtag, so "ko-KR" finds Korean and "pt-BR" finds Brazilian Portuguese
 * before Portugal's. Chinese is the one place the primary subtag is not
 * enough — Traditional-script regions map to zh-Hant. English when nothing
 * matches: the list's lingua franca, not a guess about the team.
 */
import { TRANSLATE_LANGUAGES } from "../translate/languages.js";

const STORAGE_KEY = "kola.brainstorm.translate-to";

export const FALLBACK_TRANSLATE_LANGUAGE = "en";

/** Regions whose Chinese is written in the Traditional script. */
const TRADITIONAL_CHINESE_REGIONS = new Set(["tw", "hk", "mo"]);

/** Pure: the list entry a browser language resolves to. */
export function defaultTranslateLanguage(browserLanguage: string | undefined): string {
  const wanted = (browserLanguage ?? "").trim().toLowerCase();
  if (wanted === "") return FALLBACK_TRANSLATE_LANGUAGE;
  const exact = TRANSLATE_LANGUAGES.find((language) => language.tag.toLowerCase() === wanted);
  if (exact !== undefined) return exact.tag;
  const [primary = wanted, region] = wanted.split("-");
  if (primary === "zh" && region !== undefined && TRADITIONAL_CHINESE_REGIONS.has(region)) {
    return "zh-Hant";
  }
  const byPrimary = TRANSLATE_LANGUAGES.find(
    (language) => language.tag.toLowerCase().split("-")[0] === primary,
  );
  return byPrimary?.tag ?? FALLBACK_TRANSLATE_LANGUAGE;
}

/** The remembered pick, else the browser-derived default. Never throws. */
export function storedTranslateLanguage(): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null && stored !== "") return stored;
  } catch {
    // A browser that refuses storage still gets the default below.
  }
  return defaultTranslateLanguage(
    typeof navigator === "undefined" ? undefined : navigator.language,
  );
}

/** Remember the pick for the next answer and the next session. */
export function rememberTranslateLanguage(tag: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, tag);
  } catch {
    // Not worth a warning — the pick holds for this panel regardless.
  }
}
